import React, { useEffect, useState } from "react";
import { useActor } from "@xstate/react";
import { Loader } from "@pushpress/shared-components";
import { useSignupQuery } from "../../../../apis/authApi.hooks";
import { useGenerateCodeQuery } from "../../../../apis/loginApi.hooks";
import { toast } from "react-toastify";
import { useAnalytics } from "../../../../components/providers/AnalyticsProvider";
import { SignUpEvent } from "../../constants/signup.events";
import { useSignUpMachine } from "../../context/SignUpMachineContext";
import { getSignUpPayload } from "../../utils/signup-payload";

const ImplicitCreateFreeAccountStep: React.FC = () => {
  const machine = useSignUpMachine();
  const [state, send] = useActor(machine);
  const { formData, utm } = state.context;

  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );
  const [errorMessage, setErrorMessage] = useState<string>("");

  const signup = useSignupQuery();
  const generateCode = useGenerateCodeQuery();
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    const createAccount = async () => {
      try {
        const signupPayload = getSignUpPayload({ formData, utm });

        const email = signupPayload.user.email;
        if (!email) {
          setStatus("error");
          setErrorMessage("Email is required to create your account");
          return;
        }

        const signupResult = await signup.mutateAsync(signupPayload);

        if (signupResult.uuid) {
          send({ type: "SET_CLIENT_UUID", uuid: signupResult.uuid });
        } else {
          throw new Error("No uuid returned from signup");
        }

        if (signupResult.subdomain) {
          send({ type: "SET_SUBDOMAIN", subdomain: signupResult.subdomain });
        } else {
          throw new Error("No subdomain returned from signup");
        }

        if (signupResult.email) {
          await generateCode.mutateAsync({ email: signupResult.email });
          trackEvent(SignUpEvent.FreeSignupSubmitted, {
            email: signupResult.email,
            company: signupPayload.client.company,
          });

          setStatus("success");
          send({ type: "NEXT" });
        } else {
          throw new Error("No email returned from signup");
        }
      } catch (error) {
        console.error("Signup failed:", error);
        trackEvent(SignUpEvent.SignUpFailed, {
          step: "implicit-free",
          error: error.message,
        });

        setStatus("error");
        setErrorMessage(
          error.message || "Error creating your account. Please try again.",
        );
        toast.error("Error creating your account. Please try again.");
      }
    };

    createAccount();
  }, [formData, generateCode, send, signup, trackEvent, utm]);

  if (status === "error") {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-space-y-6 tw-py-8 tw-text-center">
        <h2 className="tw-text-xl tw-font-semibold tw-text-red-600">
          Account Creation Failed
        </h2>
        <p className="tw-text-gray-600">{errorMessage}</p>
        <button
          onClick={() => setStatus("loading")}
          className="tw-bg-blue-600 tw-text-white tw-px-6 tw-py-2 tw-rounded-md tw-font-medium"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-space-y-6 tw-py-8 tw-text-center">
      <h2 className="tw-text-xl tw-font-semibold">
        Creating Your Free Account
      </h2>
      <div className="tw-flex tw-items-center tw-justify-center tw-space-x-3">
        <Loader size={28} />
        <p className="tw-text-gray-600">
          Please wait while we set up your account...
        </p>
      </div>
    </div>
  );
};

export default ImplicitCreateFreeAccountStep;
